import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    AuditingBookmark,
    GetAuditingBookmarksRequest,
    GetAuditingBookmarksResponse,
    CreateAuditingBookmarkRequest,
    CreateAuditingBookmarkResponse,
    DeleteAuditingBookmarkRequest,
    AuditingBookmarkTargetType,
    AuditingBookmarkType,
} from '../../types/reporting';

// API functions
const getAuditingBookmarks = async (
    sessionId: string,
    input?: GetAuditingBookmarksRequest
): Promise<GetAuditingBookmarksResponse> => {
    const { data } = await axios.post(
        `${API_URL}/reporting/auditing/bookmarks`,
        input ?? {},
        { headers: { Authorization: sessionId } }
    );
    return data;
};

const createAuditingBookmark = async (
    sessionId: string,
    input: CreateAuditingBookmarkRequest
): Promise<CreateAuditingBookmarkResponse> => {
    const { data } = await axios.put(
        `${API_URL}/reporting/auditing/bookmarks`,
        input,
        { headers: { Authorization: sessionId } }
    );
    return data;
};

const deleteAuditingBookmark = async (
    sessionId: string,
    input: DeleteAuditingBookmarkRequest
): Promise<void> => {
    await axios.delete(`${API_URL}/reporting/auditing/bookmarks`, {
        headers: { Authorization: sessionId },
        data: input
    });
};

// Query key factory
const bookmarksQueryKey = ['reporting', 'auditing', 'bookmarks'];

// Hook to fetch bookmarks
export const useAuditingBookmarks = (options?: GetAuditingBookmarksRequest) => {
    const sessionContext = useContext(SessionContext);

    return useQuery({
        queryKey: [...bookmarksQueryKey, options],
        queryFn: () => getAuditingBookmarks(sessionContext?.sessionId ?? '', options),
        enabled: !!sessionContext?.sessionId,
        staleTime: 1000 * 60 * 5,
    });
};

// Hook to create bookmark with optimistic update
export const useCreateAuditingBookmark = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: CreateAuditingBookmarkRequest) =>
            createAuditingBookmark(sessionContext?.sessionId ?? '', input),

        onMutate: async (newBookmark) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({ queryKey: bookmarksQueryKey });

            // Snapshot the previous value
            const previousBookmarks = queryClient.getQueryData<GetAuditingBookmarksResponse>(
                [...bookmarksQueryKey, undefined]
            );

            // Optimistically update the cache
            const optimisticBookmark: AuditingBookmark = {
                id: `temp-${Date.now()}`, // Temporary ID
                userId: sessionContext?.userId ?? '',
                targetId: newBookmark.targetId,
                orderId: newBookmark.orderId,
                targetType: newBookmark.targetType,
                bookmarkType: newBookmark.bookmarkType,
                notes: newBookmark.notes,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                roNumber: newBookmark.roNumber,
                vehicleMake: newBookmark.vehicleMake,
                insuranceName: newBookmark.insuranceName,
                partName: newBookmark.partName,
            };

            queryClient.setQueryData<GetAuditingBookmarksResponse>(
                [...bookmarksQueryKey, undefined],
                (old) => ({
                    bookmarks: [optimisticBookmark, ...(old?.bookmarks ?? [])],
                })
            );

            return { previousBookmarks };
        },

        onError: (_err, _newBookmark, context) => {
            // Rollback on error
            if (context?.previousBookmarks) {
                queryClient.setQueryData(
                    [...bookmarksQueryKey, undefined],
                    context.previousBookmarks
                );
            }
        },

        onSettled: () => {
            // Refetch after mutation
            queryClient.invalidateQueries({ queryKey: bookmarksQueryKey });
        },
    });
};

// Hook to delete bookmark with optimistic update
export const useDeleteAuditingBookmark = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: DeleteAuditingBookmarkRequest) =>
            deleteAuditingBookmark(sessionContext?.sessionId ?? '', input),

        onMutate: async (deletedBookmark) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({ queryKey: bookmarksQueryKey });

            // Snapshot the previous value
            const previousBookmarks = queryClient.getQueryData<GetAuditingBookmarksResponse>(
                [...bookmarksQueryKey, undefined]
            );

            // Optimistically remove from cache
            queryClient.setQueryData<GetAuditingBookmarksResponse>(
                [...bookmarksQueryKey, undefined],
                (old) => ({
                    bookmarks: (old?.bookmarks ?? []).filter(
                        (b) =>
                            !(
                                b.targetId === deletedBookmark.targetId &&
                                b.targetType === deletedBookmark.targetType &&
                                b.bookmarkType === deletedBookmark.bookmarkType
                            )
                    ),
                })
            );

            return { previousBookmarks };
        },

        onError: (_err, _deletedBookmark, context) => {
            // Rollback on error
            if (context?.previousBookmarks) {
                queryClient.setQueryData(
                    [...bookmarksQueryKey, undefined],
                    context.previousBookmarks
                );
            }
        },

        onSettled: () => {
            // Refetch after mutation
            queryClient.invalidateQueries({ queryKey: bookmarksQueryKey });
        },
    });
};

// Helper to check if item is bookmarked
export const isBookmarked = (
    bookmarks: AuditingBookmark[] | undefined,
    targetId: string,
    targetType: AuditingBookmarkTargetType,
    bookmarkType: AuditingBookmarkType
): boolean => {
    if (!bookmarks) return false;
    return bookmarks.some(
        (b) =>
            b.targetId === targetId &&
            b.targetType === targetType &&
            b.bookmarkType === bookmarkType
    );
};

// Helper to get bookmark types for an item
export const getBookmarkTypes = (
    bookmarks: AuditingBookmark[] | undefined,
    targetId: string,
    targetType: AuditingBookmarkTargetType
): { isSaved: boolean; isFlagged: boolean } => {
    if (!bookmarks) return { isSaved: false, isFlagged: false };
    
    const itemBookmarks = bookmarks.filter(
        (b) => b.targetId === targetId && b.targetType === targetType
    );
    
    return {
        isSaved: itemBookmarks.some((b) => b.bookmarkType === 'saved'),
        isFlagged: itemBookmarks.some((b) => b.bookmarkType === 'flagged'),
    };
};
